'use client';

import { useSubscriptionBubbleStore } from '../../store/subscriptionBubbleStore';
import { cn } from '../../utils/cn';
import { FIXED_BOTTOM_HEIGHT } from '../../utils/common/fixed-bottom-height';
import { SAFE_AREA_INSET_BOTTOM } from '../../utils/common/safe-area';
import { STICKY_BOTTOM_IDS } from '../../utils/common/sticky-bottom-ids';
import { Z_INDEX } from '../../utils/common/zIndex';
import { AnytimeGtm } from '../../utils/gtm/anytimeGtm';
import { MyPageGtm } from '../../utils/gtm/mypage/myPageGtm';
import { UstoreGtm } from '../../utils/gtm/ustore/ustoreGtm';
import { useBottomNavigation } from '../../utils/hook/react-native';
import { useHasUnreadNotificationsQuery } from '../../utils/hook/react-query/recipe/useHasUnreadNotificationsQuery';
import { useMySubscriptionsQuery } from '../../utils/hook/react-query/useMySubscriptionsQuery';
import useMeQuery from '../../utils/hook/react-query/user/useMeQuery';
import { useHistoryMethod } from '../../utils/hook/useHistoryMethod';
import { usePathnameMethod } from '../../utils/hook/usePathnameMethod';
import useRegisterStickyBottom from '../../utils/hook/useRegisterStickyBottom';

import BottomNavIcon, { type BottomNavPath } from './BottomNavIcon';
import Bubble from './Bubble';
import useRefrigeratorNotification from './subscription/refrigerator/hook/useRefrigeratorNotification';

const BottomNavigation = () => {
  const ref = useRegisterStickyBottom(STICKY_BOTTOM_IDS.BOTTOM_NAVIGATION);
  const isClosed = useSubscriptionBubbleStore.use.isClosed();
  const closeBubble = useSubscriptionBubbleStore.use.closeBubble();
  const { me } = useMeQuery();
  const { hasUnreadNotifications: hasUnreadRecipeNotifications } =
    useHasUnreadNotificationsQuery();
  const { data: subscriptions = [] } = useMySubscriptionsQuery();

  const history = useHistoryMethod();
  const { isShowBottomNavigation } = useBottomNavigation();
  const { pathnameIncludes } = usePathnameMethod();
  const {
    hasUnreadNotifications: hasRefrigeratorNotification,
    readNotification: readRefrigeratorNotification,
  } = useRefrigeratorNotification();

  const navBarMenu: BottomNavPath[] = [
    '/ustore',
    '/recipes',
    '/main',
    '/refrigerator',
    '/mypages',
  ];

  /**
   * 바텀 네비게이션에서 정기배송 버튼 클릭 시 타겟 경로를 결정하는 함수
   * - 정기배송 이력이 있으면 → /subscriptions/{id}/main (SubscriptionMainPage)
   * - 정기배송 이력이 없으면 → /main (MainPage)
   */
  const getSubscriptionTargetPath = (): string => {
    // 정기배송 이력이 있는 경우 첫 번째 구독의 SubscriptionMainPage로 이동
    if (subscriptions && subscriptions.length > 0) {
      return `/subscriptions/${subscriptions[0].id}/main`;
    }

    // 정기배송 이력이 없으면 기존 MainPage
    return '/main';
  };

  /**
   * 경로별 GTM 이벤트 및 부가 작업을 처리하는 함수
   */
  const handlePathSideEffects = (path: string) => {
    const ustoreGtm = new UstoreGtm();
    const myPageGtm = new MyPageGtm();
    const anytimeGtm = new AnytimeGtm();

    switch (path) {
      case '/ustore':
        ustoreGtm.click_ustore_menu();
        break;
      case '/recipes':
        myPageGtm.click_community_menu();
        break;
      case '/refrigerator':
        anytimeGtm.click_my_refri();
        readRefrigeratorNotification();
        break;
      default:
        break;
    }
  };

  const pushToPath = (originalPath: string) => {
    // originalPath가 '/main'인 경우 유저에 따라 path가 다르기 때문에 예외처리
    if (originalPath !== '/main' && pathnameIncludes(originalPath)) return;

    // 경로별 부가 작업 처리 (GTM, 알림 읽기 등)
    handlePathSideEffects(originalPath);

    let targetPath = originalPath;

    switch (originalPath) {
      case '/main':
        // 바텀 네비게이션에서 정기배송 클릭 시만 분기 처리
        targetPath = getSubscriptionTargetPath();
        break;
      case '/mypages':
        targetPath = me ? '/mypages' : '/login';
        break;
      default:
        break;
    }

    history.push(targetPath);
  };

  const checkHasUnreadNotifications = (path: BottomNavPath) => {
    switch (path) {
      case '/recipes':
        return hasUnreadRecipeNotifications;
      case '/refrigerator':
        return hasRefrigeratorNotification;
      default:
        return false;
    }
  };

  if (!isShowBottomNavigation || typeof window === 'undefined') {
    return <></>;
  }

  return (
    <>
      <div
        className="w-full"
        style={{
          height: FIXED_BOTTOM_HEIGHT.BOTTOM_NAVIGATION,
        }}
      >
        <div style={{ height: SAFE_AREA_INSET_BOTTOM }} />
        {/* 외부 div: 배경과 그림자가 전체 너비에 적용 */}
        <div
          ref={ref}
          className="fixed inset-x-0 bottom-0 bg-white shadow-[0_-4px_8px_0_rgba(0,0,0,0.05)]"
          style={{
            zIndex: Z_INDEX.BOTTOM_NAVIGATION,
            paddingBottom: SAFE_AREA_INSET_BOTTOM,
          }}
        >
          {/* 내부 div: 컨텐츠는 중앙 정렬 + 너비 제한 */}
          <div
            className="mx-auto flex w-full items-center justify-between pt-[4px] sm:min-w-[480px] sm:max-w-[480px]"
            style={{ height: FIXED_BOTTOM_HEIGHT.BOTTOM_NAVIGATION }}
          >
            {navBarMenu.map((path) => (
              <div key={path} className="relative flex flex-1 justify-center">
                <button
                  className={cn(
                    'relative w-full grid place-items-center',
                    path === '/main' && 'mb-[7px] rounded-[100px]',
                  )}
                  onClick={() => pushToPath(path)}
                  type="button"
                >
                  <BottomNavIcon
                    path={path}
                    isNotificationOn={checkHasUnreadNotifications(path)}
                  />
                </button>

                {/* 정기배송 버튼에 Bubble 추가 */}
                {path === '/main' && !isClosed && me?.isSubscribing && (
                  <div className="absolute bottom-[calc(100%+8px)] left-1/2">
                    <Bubble
                      color="#926E5F"
                      arrowPosition="bottom"
                      tailPosition="left"
                      showCloseButton={true}
                      onClose={closeBubble}
                      isVisible={!isClosed}
                      className="whitespace-nowrap"
                    >
                      <p className="text-[14px] font-medium text-white">
                        새로 생겼어요🎉
                      </p>
                    </Bubble>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default BottomNavigation;
